(() => {
  'use strict';
  if (window.MulamieCartGifts) return;
  window.MulamieCartGifts = true;
  const root = window.Shopify?.routes?.root || '/';
  let busy = false;
  let failed = false;
  let observerTimer;

  function getProperties(control) {
    try {
      const parsed = JSON.parse(control.dataset.properties || '{}');
      // Liquid serializes line-item properties as pairs, for example
      // [["__fbBundleClick", "…"]]. Shopify's cart/change endpoint requires
      // an object. Convert this representation so bundle metadata survives.
      if (Array.isArray(parsed)) return Object.fromEntries(parsed.filter(pair => Array.isArray(pair) && pair.length === 2));
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) {
      return {};
    }
  }

  // Keep the fetch-based product submission used by the bundle app, while
  // moving gift selection entirely to the cart. Do not merge lines ourselves.
  async function addProduct(form) {
    if (form.dataset.giftAdding) return;
    const button = form.querySelector('[name="add"]');
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
    form.dataset.giftAdding = 'true';
    button.classList.add('is--loading');
    const data = new FormData(form);
    button.disabled = true;
    let errorNode = form.querySelector('[data-gift-add-error]');
    if (errorNode) errorNode.remove();
    try {
      const response = await fetch(root + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: data });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'No se pudo agregar el producto.');
      if (window.T4SThemeSP?.Cart?.getToFetch) window.T4SThemeSP.Cart.getToFetch(true);
      else location.assign(root + 'cart');
    } catch (error) {
      errorNode = document.createElement('p');
      errorNode.dataset.giftAddError = '';
      errorNode.setAttribute('role', 'alert');
      errorNode.textContent = error.message;
      form.append(errorNode);
    } finally {
      button.disabled = false;
      button.classList.remove('is--loading');
      delete form.dataset.giftAdding;
    }
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('form[data-mulamie-add="main"] [name="add"]');
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    addProduct(button.form);
  }, true);
  document.addEventListener('submit', event => {
    if (!event.target.matches('form[data-mulamie-add="main"]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    addProduct(event.target);
  }, true);

  async function request(path, body) {
    const response = await fetch(root + path, {
      method: body ? 'POST' : 'GET',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : { cache: 'no-store' })
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || 'No se pudo guardar el empaque.');
    return result;
  }

  function status(control, message, error = false) {
    const node = control.querySelector('[data-gift-status]');
    node.textContent = message;
    node.classList.toggle('is-error', error);
  }

  function setBusy(value) {
    busy = value;
  }

  function getSections() {
    const page = /\/cart\/?$/.test(location.pathname) ? 'main-cart' : 'mini_cart';
    return { page, names: ['cart_data', page] };
  }

  async function refresh() {
    const { page, names } = getSections();
    const url = new URL(location.href);
    url.searchParams.set('sections', names.join(','));
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error('No se pudo actualizar el carrito. Recarga la página.');
    const sections = await response.json();
    if (!sections.cart_data || !sections[page] || !window.T4SThemeSP?.Cart?.renderContents) {
      throw new Error('Recarga la página para comprobar tu selección de regalo.');
    }
    window.T4SThemeSP.Cart.renderContents(sections, false);
  }

  function showPendingState(control, count) {
    const check = control.querySelector('[data-gift-check]');
    const select = control.querySelector('[data-gift-quantity]');
    const total = Number(control.dataset.quantity);
    if (check) check.checked = count > 0;
    if (select) select.hidden = !(count > 0 && total > 1);
    if (select && count > 0) select.value = String(count);
    status(control, 'Guardando tu selección…');
  }

  function renderUpdatedCart(result) {
    const sections = result.sections;
    if (!sections || !window.T4SThemeSP?.Cart?.renderContents) return false;
    const { page } = getSections();
    if (!sections.cart_data || !sections[page]) return false;
    window.T4SThemeSP.Cart.renderContents(sections, false);
    return true;
  }

  async function save(control, requestedCount) {
    if (busy) return;
    const count = Math.min(Number(control.dataset.quantity), Math.max(0, Math.floor(Number(requestedCount) || 0)));
    setBusy(true);
    control.setAttribute('aria-busy', 'true');
    showPendingState(control, count);
    try {
      // Properties come from the rendered cart line, preserving bundle metadata
      // without an extra cart.js request before every selection.
      const properties = getProperties(control);
      // Empty values remove only our visible properties; never erase bundle data.
      properties['Para regalo'] = count ? 'Sí' : '';
      properties['Unidades para regalo'] = count ? String(count) : '';
      properties.Empaque = count ? 'Incluido sin costo' : '';
      const { names } = getSections();
      const result = await request('cart/change.js', {
        id: control.dataset.key,
        quantity: Number(control.dataset.quantity),
        properties,
        sections: names,
        sections_url: location.pathname
      });
      if (!renderUpdatedCart(result)) await refresh();
      failed = false;
    } catch (error) {
      failed = true;
      status(control, error.message + ' Vuelve a intentarlo antes de pagar.', true);
    } finally {
      control.setAttribute('aria-busy', 'false');
      setBusy(false);
      scheduleNormalize();
    }
  }

  // Native quantity edits re-render the controls. Clamp a prior gift count if
  // the quantity is reduced; never silently mark newly added units as gifts.
  function scheduleNormalize() {
    clearTimeout(observerTimer);
    observerTimer = setTimeout(() => {
      if (busy || failed) return;
      const invalid = [...document.querySelectorAll('[data-gift-control]')].find(node =>
        Number(node.dataset.giftCount) > Number(node.dataset.quantity));
      if (invalid) save(invalid, Number(invalid.dataset.quantity));
    }, 100);
  }

  document.addEventListener('change', event => {
    const control = event.target.closest('[data-gift-control]');
    if (!control) return;
    if (event.target.matches('[data-gift-check]')) {
      // Most gift purchases include one wrapped unit. Start there instead of
      // briefly saving every unit as a gift and forcing a second update.
      save(control, event.target.checked ? 1 : 0);
    } else if (event.target.matches('[data-gift-quantity]')) {
      save(control, event.target.value);
    }
  });

  function pendingQuantityChange() {
    return [...document.querySelectorAll('[data-gift-control]')].some(control => {
      const input = control.closest('[data-cart-item]')?.querySelector('[data-quantity-value]');
      return input && Number(input.value) !== Number(control.dataset.quantity);
    });
  }

  // Prevent checkout or overlapping cart edits while a gift change is saving.
  document.addEventListener('click', event => {
    const target = event.target.closest('button,a,input,select');
    if (!target) return;
    const checkout = target.matches('[name="checkout"], [href*="/checkout"], [data-checkout]');
    const cartAction = target.closest('[data-cart-wrapper]');
    if ((busy && cartAction) || (checkout && (failed || pendingQuantityChange()))) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (failed) document.querySelector('[data-gift-status].is-error')?.scrollIntoView({ block: 'center' });
    }
    if (target.closest('[data-gift-control]') && pendingQuantityChange()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  document.addEventListener('submit', event => {
    if (event.target.closest('[data-cart-wrapper]') && (busy || failed || pendingQuantityChange())) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  document.addEventListener('click', event => {
    const link = event.target.closest('[data-gift-photo]');
    if (!link || typeof HTMLDialogElement === 'undefined') return;
    event.preventDefault();
    let dialog = document.querySelector('.mulamie-gift-dialog');
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'mulamie-gift-dialog';
      dialog.setAttribute('aria-label', 'Empaque de regalo Mulamie');
      dialog.innerHTML = '<button type="button">Cerrar</button><h2>Un detalle especial</h2><img alt="Empaque de regalo Mulamie"><p>Empaque incluido sin costo. Elige las prendas que son para regalo en el carrito.</p>';
      dialog.querySelector('button').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', e => { if (e.target === dialog) dialog.close(); });
      document.body.append(dialog);
    }
    dialog.querySelector('img').src = link.href;
    dialog.showModal();
  });

  new MutationObserver(mutations => {
    if (mutations.some(m => [...m.addedNodes].some(node => node.nodeType === 1 &&
      (node.matches('[data-gift-control]') || node.querySelector('[data-gift-control]'))))) scheduleNormalize();
  }).observe(document.body, { childList: true, subtree: true });
  scheduleNormalize();
})();
